import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App.jsx'
import './index.css'

// Session Initialization: Ensured by index.html blocking script
const sessionId = sessionStorage.getItem('hs_session_id') || 'gen-' + Date.now();

import { ToastProvider } from './context/ToastContext'

import { registerSW } from 'virtual:pwa-register'

const updateSW = registerSW({
    onNeedRefresh() {
        // Instead of silent immediate refresh, we let the service worker update.
        // The user will get the new version either on next navigation or if they explicitly refresh.
        console.log('[PWA] New content available. Update will apply on next load.');
        // If we really want to force it, we can call updateSW(true) here, 
        // but for high-stabiliy background apps, it's better to be passive.
    },
    onOfflineReady() {
        console.log('[PWA] App ready for offline use');
    },
    onRegistered(registration) {
        console.log('[PWA] Service Worker registered');
        // Send session ID to Service Worker
        if (registration && registration.active) {
            registration.active.postMessage({ type: 'SET_SESSION_ID', sessionId });
        }
        
        // Check for updates every 60 minutes (less aggressive)
        if (registration) {
            setInterval(() => {
                registration.update();
            }, 60 * 60 * 1000);
        }
    }
})

import ErrorBoundary from './components/ErrorBoundary';

import { AppProvider } from './context/AppContext'

ReactDOM.createRoot(document.getElementById('root')).render(
    <React.StrictMode>
        <ToastProvider>
            <AppProvider>
                <ErrorBoundary>
                    <App />
                </ErrorBoundary>
            </AppProvider>
        </ToastProvider>
    </React.StrictMode>,
)

// Force reload when a new service worker takes control
// Guard against rapid reload loops (especially on iOS)
const lastReload = sessionStorage.getItem('hs_last_reload');
const now = Date.now();
let refreshing = false;

if ('serviceWorker' in navigator) {
    if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'SET_SESSION_ID', sessionId });
    }
    
    navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (refreshing) return;
        
        // Prevent reloading if we reloaded in the last 5 seconds (prevents loops)
        if (lastReload && (now - parseInt(lastReload)) < 5000) {
            console.log('[PWA] Suppressing rapid reload loop');
            return;
        }

        refreshing = true;
        sessionStorage.setItem('hs_last_reload', Date.now().toString());
        console.log('[PWA] New version detected, reloading app in 200ms...');
        
        // Small delay to allow the new service worker to fully stabilize
        setTimeout(() => {
            window.location.reload();
        }, 200);
    });
}

// Auto-reload on chunk load errors (updates) & report uncaught errors to centralized error logger
window.addEventListener('error', (e) => {
    // Check if the error is about loading a chunk (e.g. 404 on main.js or lazy routes)
    if (/Loading chunk [\d]+ failed/.test(e.message) || /Importing a module script failed/.test(e.message)) {
        console.log('[App] Chunk load error detected, forcing reload...');
        e.preventDefault();
        window.location.reload();
        return;
    }

    // Ignore benign cross-origin script or browser extension errors
    if (!e.message || e.message === 'Script error.') return;

    try {
        fetch('/api/logs/error', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                message: `Uncaught Exception: ${e.message}`,
                stack: e.error?.stack || `${e.filename || 'unknown'}:${e.lineno || 0}:${e.colno || 0}`,
                component: 'GlobalWindow',
                url: window.location.href,
                version: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'unknown',
                meta: { filename: e.filename, lineno: e.lineno, colno: e.colno }
            })
        }).catch(() => {});
    } catch (_) {}
}, true); // Use capture phase to catch resource errors

window.addEventListener('unhandledrejection', (e) => {
    try {
        const error = e.reason;
        const msg = error?.message || (typeof error === 'string' ? error : 'Unhandled Promise Rejection');
        fetch('/api/logs/error', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                message: `Unhandled Rejection: ${msg}`,
                stack: error?.stack || null,
                component: 'UnhandledPromise',
                url: window.location.href,
                version: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'unknown',
                meta: { reason: String(e.reason) }
            })
        }).catch(() => {});
    } catch (_) {}
});
